import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, fileUrl } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import {
  Camera,
  Upload,
  FileCsv,
  Sparkle,
  Plus,
  Trash,
  Check,
  ClockCounterClockwise,
  ArrowLeft,
  PencilSimple,
  ArrowsMerge,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { buildProductIndex, matchProducts } from "../lib/productMatch";

export default function IntakePage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user, isSuperOwner } = useAuth();
  // v403.5 — god mode: owner/admin can edit + delete ANY intake record.
  // v406.2 — allowlisted super-owners (Harvey/Sukhinder) get god-mode parity.
  const canManageHistory = !!user && user !== false && (["owner", "admin"].includes(user.role) || isSuperOwner);
  const [mode, setMode] = useState(null); // 'image' | 'csv'
  const [items, setItems] = useState([]); // [{name, quantity, unit}]
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [histTotal, setHistTotal] = useState(0);
  const [histQ, setHistQ] = useState("");
  // v405.8 Scan & Send — optional destination: received into master then
  // auto-consigned to this distributor (empty = keep in master).
  const [distributors, setDistributors] = useState([]);
  const [sendTo, setSendTo] = useState("");
  // v405.9 — catalog names for the review-screen autocomplete (fix AI
  // misreads like 'MT1' by picking 'Melanotan I 10mg' in two taps).
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get("/distributors")
      .then(({ data }) => setDistributors(data || []))
      .catch(() => {});
    api.get("/products")
      .then(({ data }) => setCatalog((data || []).map((p) => p.name).sort((a, b) => a.localeCompare(b))))
      .catch(() => {});
  }, []);

  const loadHistory = async (opts = {}) => {
    const reset = opts.reset !== false;
    try {
      const { data } = await api.get("/inventory/intake-history", {
        params: { limit: 50, skip: reset ? 0 : history.length, q: histQ.trim() || undefined },
      });
      setHistTotal(data.total || 0);
      setHistory(reset ? (data.items || []) : [...history, ...(data.items || [])]);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [histQ]);

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setMode("image");
    setItems([]);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/inventory/scan-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems(data.items || []);
      setPhotoPath(data.photo_path || null);
      if ((data.items || []).length === 0) {
        toast.warning(t("intake.no_items_image"));
      } else {
        toast.success(t("intake.found_items", { count: data.items.length }));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMode("csv");
    setItems([]);
    setPhotoPath(null);
    setPhotoPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/inventory/parse-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems(data.items || []);
      if ((data.items || []).length === 0) {
        toast.warning(t("intake.no_items_csv"));
      } else {
        toast.success(t("intake.loaded_rows", { count: data.row_count }));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateItem = (idx, field, value) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setItems((arr) => [...arr, { name: "", quantity: 1, unit: "vials" }]);

  // v409.3 — manual entry: skip the scan entirely and type rows with smart
  // shorthand matching ("reta 10" -> Retatrutide 10mg, "bb20" -> Wolverine).
  const startManual = () => {
    setMode("manual");
    setPhotoPath(null);
    setPhotoPreview(null);
    setItems((arr) => (arr.length > 0 ? arr : [{ name: "", quantity: 1, unit: "vials" }]));
  };

  // v403.5 — god-mode intake history actions
  const editHistoryQty = async (h) => {
    const raw = window.prompt(t("intake.edit_qty_prompt", { name: h.product_name }), h.quantity);
    if (raw === null) return;
    const qty = parseInt(raw, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      toast.error(t("intake.invalid_qty"));
      return;
    }
    if (qty === h.quantity) return;
    try {
      await api.patch(`/inventory/intake/${h.id}`, { quantity: qty });
      toast.success(t("intake.edited_toast", { name: h.product_name }));
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteHistory = async (h) => {
    if (!window.confirm(t("intake.delete_confirm", { qty: h.quantity, name: h.product_name }))) return;
    try {
      const { data } = await api.delete(`/inventory/intake/${h.id}`);
      toast.success(t("intake.deleted_toast", { n: data.stock_adjusted, name: h.product_name }));
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const commit = async () => {
    const cleaned = items
      .map((it) => ({
        name: (it.name || "").trim(),
        quantity: Math.max(0, parseInt(it.quantity, 10) || 0),
        unit: (it.unit || "units").trim() || "units",
      }))
      .filter((it) => it.name && it.quantity > 0);

    if (cleaned.length === 0) {
      toast.error(t("intake.min_one_item"));
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/inventory/intake", {
        items: cleaned,
        source: mode || "manual",
        photo_path: photoPath,
        send_to_distributor_id: sendTo || null,
      });
      // v414.58 — duplicate guard: some lines may have been held back because
      // the same product already received the same quantity minutes ago
      // (Slupp 332 double-count incident). Confirm per batch, then force-add.
      const dups = data.duplicate_suspects || [];
      if (dups.length > 0) {
        const listing = dups
          .map((d) => `• ${d.name}: +${d.quantity} ${d.unit} (same amount already added at ${new Date(d.prior_intake_at).toLocaleTimeString()})`)
          .join("\n");
        const addAnyway = window.confirm(
          `${t("intake.duplicate_guard_title")}\n\n${listing}\n\n${t("intake.duplicate_guard_question")}`);
        if (addAnyway) {
          const forced = cleaned
            .filter((c) => dups.some((d) => d.name.toLowerCase().includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(d.name.toLowerCase())))
            .map((c) => ({ ...c, force_duplicate: true }));
          const { data: d2 } = await api.post("/inventory/intake", {
            items: forced,
            source: mode || "manual",
            photo_path: photoPath,
            send_to_distributor_id: sendTo || null,
          });
          data.items = data.items.filter((i) => !i.duplicate_suspect).concat(d2.items);
          toast.success(t("intake.duplicate_added", { n: forced.length }));
        } else {
          toast.info(t("intake.duplicate_skipped", { n: dups.length }));
          data.items = data.items.filter((i) => !i.duplicate_suspect);
        }
      }
      const created = data.items.filter((i) => i.action === "created").length;
      const merged = data.items.filter((i) => i.action === "merged").length;
      if (data.sent_to) {
        toast.success(t("intake.recorded_sent", { created, merged, name: data.sent_to.name }));
        if (data.send_failed_count > 0) {
          toast.error(t("intake.send_failed_some", { n: data.send_failed_count }), {
            description: (data.items || [])
              .filter((i) => i.sent === false)
              .map((i) => `${i.name}: ${i.send_error || ""}`)
              .join("\n"),
            duration: 8000,
          });
        }
      } else {
        toast.success(t("intake.recorded", { created, merged }));
      }
      // v414.22 — new cards have no prices yet: without a retail they show
      // as "Ask" on distributor stores and can't be ordered.
      if (created > 0) {
        toast.warning(t("intake.no_price_warning", { count: created }), {
          description: (data.items || [])
            .filter((i) => i.action === "created")
            .map((i) => i.name)
            .join(", "),
          duration: 10000,
        });
      }
      setItems([]);
      setPhotoPath(null);
      setPhotoPreview(null);
      setMode(null);
      setSendTo("");
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="intake-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Link
            to="/inventory"
            className="inline-flex items-center gap-2 font-medium text-sm hover:underline mb-2"
          >
            <ArrowLeft size={16} weight="bold" /> {t("intake.back_to_inventory")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("intake.title")}</h1>
          <p className="text-sm sm:text-base text-zinc-600 mt-1">
            {t("intake.subtitle")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ModeCard
          title={t("intake.scan_title")}
          subtitle={t("intake.scan_subtitle")}
          icon={Camera}
          accent="#dbeafe"
          testId="mode-image"
          loading={scanning && mode === "image"}
          onFile={handleImage}
          accept="image/*"
        />
        <ModeCard
          title={t("intake.csv_title")}
          subtitle={t("intake.csv_subtitle")}
          icon={FileCsv}
          accent="#fafafa"
          testId="mode-csv"
          onFile={handleCsv}
          accept=".csv,text/csv"
        />
        <button
          type="button"
          onClick={startManual}
          data-testid="mode-manual"
          className="text-left cursor-pointer block bg-white border border-zinc-200 rounded-xl shadow-sm p-6 transition-all hover:-translate-y-px hover:shadow-lg"
        >
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 border border-zinc-200 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "#fbf6e7" }}
            >
              <PencilSimple size={28} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-semibold tracking-tight">{t("intake.manual_title")}</h3>
              <p className="text-sm text-zinc-600 mt-1">{t("intake.manual_subtitle")}</p>
              <div className="mt-4 inline-flex items-center gap-2 font-semibold text-sm">
                <Plus size={16} weight="bold" /> {t("intake.manual_start")}
              </div>
            </div>
          </div>
        </button>
      </div>

      {scanning && (
        <BrutalCard className="bg-[#c9a24a]/15">
          <div className="flex items-center gap-3">
            <Sparkle size={24} weight="fill" className="animate-spin" />
            <p className="font-semibold ">{t("intake.reading_image")}</p>
          </div>
        </BrutalCard>
      )}

      {items.length > 0 && (
        <section data-testid="intake-review-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("intake.review_title")}</h2>
              <p className="text-sm text-zinc-600 font-medium">
                {t("intake.review_subtitle")}
              </p>
              <p
                className="text-xs font-semibold text-[#8a6a1c] mt-1.5 inline-flex items-center gap-1.5 bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-full px-2.5 py-1"
                data-testid="intake-edit-hint"
              >
                <PencilSimple size={12} weight="bold" /> {t("intake.tap_to_edit_hint")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  {t("intake.send_to_label")}
                </label>
                <select
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  data-testid="intake-send-to-select"
                  className="input-brutal h-11 !py-0 pr-8 min-w-[220px] font-medium"
                >
                  <option value="">{t("intake.send_master_option")}</option>
                  {distributors
                    // v409.2 — new stock intake never targets personal entries
                    .filter((d) => !d.is_personal)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}{d.role === "partner" ? ` · ${t("intake.partner_tag")}` : ""}
                      </option>
                    ))}
                </select>
              </div>
              <BrutalButton size="md" variant="secondary" onClick={addItem} data-testid="intake-add-row">
                <Plus size={16} weight="bold" /> {t("intake.add_row")}
              </BrutalButton>
              <BrutalButton size="md" onClick={commit} disabled={committing} data-testid="intake-commit-button">
                {sendTo ? <PaperPlaneTilt size={16} weight="bold" /> : <Check size={16} weight="bold" />}
                {committing
                  ? t("common.saving")
                  : sendTo
                    ? t("intake.save_and_send")
                    : t("intake.save_to_inventory")}
              </BrutalButton>
            </div>
          </div>

          {sendTo && (
            <div
              className="mb-4 flex items-start gap-2 bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2 text-sm font-medium text-[#8a6a1c]"
              data-testid="intake-send-hint"
            >
              <PaperPlaneTilt size={16} weight="bold" className="mt-0.5 shrink-0" />
              <span>
                {t("intake.send_hint", {
                  name: distributors.find((d) => d.id === sendTo)?.name || "",
                })}
              </span>
            </div>
          )}

          {photoPreview && (
            <BrutalCard className="!p-3 mb-4">
              <div className="flex gap-4 items-center">
                <img src={photoPreview} alt="Receipt" className="w-24 h-24 object-cover border border-zinc-200 rounded-lg" />
                <p className="text-sm text-zinc-600">
                  {t("intake.original_photo")}
                </p>
              </div>
            </BrutalCard>
          )}

          <div className="space-y-3">
            <div className="hidden md:grid grid-cols-12 gap-3 px-3 text-xs font-semibold  text-zinc-500">
              <div className="col-span-6">{t("intake.product_name")}</div>
              <div className="col-span-2">{t("intake.quantity")}</div>
              <div className="col-span-3">{t("inventory.unit")}</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((it, idx) => (
              <div
                key={idx}
                data-testid={`intake-row-${idx}`}
                className="bg-white border border-zinc-200 rounded-xl p-3 shadow-sm grid grid-cols-12 gap-3"
              >
                <div className="col-span-12 md:col-span-6">
                  <label className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">
                    {t("intake.product_name")}
                  </label>
                  <NameSuggestInput
                    value={it.name}
                    onChange={(v) => updateItem(idx, "name", v)}
                    catalog={catalog}
                    idx={idx}
                  />
                  {it.merges_into && it.merges_into.toLowerCase() !== (it.name || "").trim().toLowerCase() && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                      data-testid={`intake-merge-hint-${idx}`}
                    >
                      <ArrowsMerge size={11} weight="bold" /> {t("intake.merge_hint", { name: it.merges_into })}
                    </div>
                  )}
                  {it.boxes_seen > 0 && (
                    <div
                      className="mt-1 ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-full px-2 py-0.5"
                      data-testid={`intake-box-hint-${idx}`}
                    >
                      {t("intake.box_count_hint", { boxes: it.boxes_seen, per: it.units_per_box, total: it.boxes_seen * it.units_per_box })}
                    </div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">
                    {t("intake.quantity")}
                  </label>
                  <BrutalInput
                    type="number"
                    min="1"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                    data-testid={`intake-qty-${idx}`}
                  />
                </div>
                <div className="col-span-5 md:col-span-3">
                  <label className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">
                    {t("inventory.unit")}
                  </label>
                  <BrutalInput
                    value={it.unit}
                    onChange={(e) => updateItem(idx, "unit", e.target.value)}
                    placeholder={t("common.units")}
                  />
                </div>
                <button
                  onClick={() => removeItem(idx)}
                  data-testid={`intake-remove-${idx}`}
                  className="col-span-1 h-14 self-end bg-red-600 text-white border border-zinc-200 rounded-lg shadow-sm flex items-center justify-center"
                  aria-label="Remove"
                >
                  <Trash size={18} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ClockCounterClockwise size={24} weight="bold" /> {t("intake.history_title")}
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c] tabular-nums" data-testid="intake-history-total">
              {histTotal}
            </span>
          </h2>
          <input
            className="input-brutal w-full sm:w-64"
            value={histQ}
            onChange={(e) => setHistQ(e.target.value)}
            placeholder={t("intake.history_search")}
            data-testid="intake-history-search"
          />
        </div>
        {history.length === 0 ? (
          <BrutalCard><p className="font-medium">{t("intake.no_intake")}</p></BrutalCard>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div
                key={h.id}
                data-testid={`history-${h.id}`}
                className="bg-white border border-zinc-200 rounded-lg p-3 shadow-sm flex flex-wrap items-center justify-between gap-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {h.photo_path ? (
                    <img
                      src={fileUrl(h.photo_path)}
                      alt=""
                      className="w-10 h-10 object-cover border border-zinc-200 rounded"
                      crossOrigin="use-credentials"
                    />
                  ) : (
                    <div className="w-10 h-10 border border-zinc-200 rounded bg-[#c9a24a]/25 flex items-center justify-center">
                      {h.source === "csv" ? <FileCsv size={18} weight="bold" /> : h.source === "image" ? <Camera size={18} weight="bold" /> : <Plus size={18} weight="bold" />}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{h.product_name}</div>
                    <div className="text-xs font-medium text-zinc-600">
                      +{h.quantity} {h.unit || t("common.units")} · {h.action} · {h.source}
                      {h.matched_from && (
                        <span className="text-emerald-700"> · {t("intake.matched_from", { name: h.matched_from })}</span>
                      )}
                      {h.edited && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-bold tracking-wider uppercase align-middle"
                          data-testid={`intake-edited-badge-${h.id}`}
                        >
                          <PencilSimple size={9} weight="bold" /> {t("edit_log.edited_badge")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium text-zinc-500 tabular-nums">
                    {formatDate(h.created_at)}
                  </div>
                  {canManageHistory && (
                    <>
                      <button
                        onClick={() => editHistoryQty(h)}
                        className="text-zinc-400 hover:text-zinc-800 p-1"
                        title={t("intake.edit_record")}
                        data-testid={`intake-edit-${h.id}`}
                      >
                        <PencilSimple size={15} weight="bold" />
                      </button>
                      <button
                        onClick={() => deleteHistory(h)}
                        className="text-zinc-400 hover:text-red-600 p-1"
                        title={t("intake.delete_record")}
                        data-testid={`intake-delete-${h.id}`}
                      >
                        <Trash size={15} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {history.length < histTotal && (
              <div className="pt-2">
                <BrutalButton
                  variant="secondary"
                  onClick={() => loadHistory({ reset: false })}
                  data-testid="intake-history-more"
                >
                  {t("intake.load_more", { n: histTotal - history.length })}
                </BrutalButton>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// v405.9 — name input with pencil affordance + pick-from-catalog suggestions.
// v409.3 — SMART shorthand matching via lib/productMatch: "reta 10" / "rt10"
// -> Retatrutide 10mg, "bb20" -> Wolverine (BB20), "cjc" -> CJC-1295 DAC…
// Enter picks the top match; a green chip confirms an exact catalog match.
function NameSuggestInput({ value, onChange, catalog, idx }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const index = React.useMemo(() => buildProductIndex(catalog), [catalog]);
  const q = (value || "").trim().toLowerCase();
  const smart = q ? matchProducts(q, index) : catalog.slice(0, 8);
  const exact = q && catalog.some((n) => n.toLowerCase() === q);
  const showList = open && smart.length > 0 && !exact;
  const pick = (n) => { onChange(n); setOpen(false); };
  return (
    <div className="relative">
      <BrutalInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && showList) {
            e.preventDefault();
            pick(smart[0]);
          }
        }}
        placeholder={t("intake.product_name")}
        className="!pr-10"
        data-testid={`intake-name-${idx}`}
      />
      {exact ? (
        <Check
          size={16}
          weight="bold"
          className="absolute right-3.5 top-[1.75rem] -translate-y-1/2 text-emerald-600 pointer-events-none"
          data-testid={`intake-name-exact-${idx}`}
        />
      ) : (
        <PencilSimple
          size={16}
          weight="bold"
          className="absolute right-3.5 top-[1.75rem] -translate-y-1/2 text-[#c9a24a] pointer-events-none"
        />
      )}
      {showList && (
        <div
          className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl max-h-56 overflow-y-auto"
          data-testid={`intake-suggest-${idx}`}
        >
          {smart.map((n, i) => (
            <button
              key={n}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(n)}
              className="w-full text-left px-3.5 py-2.5 text-sm font-medium hover:bg-[#fbf6e7] active:bg-[#fbf6e7] border-b border-zinc-100 last:border-0"
              data-testid={`intake-suggest-${idx}-${i}`}
            >
              {n}
              {i === 0 && q && (
                <span className="ml-2 text-[10px] font-bold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-full px-1.5 py-0.5 uppercase">
                  {t("intake.press_enter")}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ModeCard({ title, subtitle, icon: Icon, accent, onFile, accept, testId, loading }) {
  const { t } = useTranslation();
  return (
    <label
      data-testid={testId}
      className="cursor-pointer block bg-white border border-zinc-200 rounded-xl shadow-sm p-6 transition-all hover:-translate-y-px hover:shadow-lg  "
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 border border-zinc-200 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: accent }}
        >
          <Icon size={28} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-2xl font-semibold tracking-tight">{title}</h3>
          <p className="text-sm text-zinc-600 mt-1">{subtitle}</p>
          <div className="mt-4 inline-flex items-center gap-2 font-semibold text-sm ">
            <Upload size={16} weight="bold" />
            {loading ? t("intake.reading") : t("intake.choose_file")}
          </div>
        </div>
      </div>
      <input type="file" className="hidden" accept={accept} onChange={onFile} disabled={loading} />
    </label>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
